import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  HelpCircle,
  LayoutGrid,
  LogOut,
  PlusCircle,
  Settings,
  User as UserIcon,
  X,
} from 'lucide-react';
import { Drawer } from '@shared/ui/containers/Drawer';
import { cn } from '@shared/lib/cn';
import { env } from '@config/env';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { routeForModule } from '@shared/lib/moduleRoutes';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { switchTenant } from '@features/auth/api';
import { joinConfig } from '@features/join/config';
import type { CompiledNavigationItem } from '@shared/types/domainConfig';

/**
 * Drawer slide-in for tenant switcher + secondary navigation. Opened from
 * the menu button on Home. A brand-gradient header carries the logo + the
 * member's profile; below it, flat rounded nav rows grouped into sections.
 * Active tenant is highlighted; tapping another linked tenant runs the
 * switch flow. The "Explore" section is data-driven from the tenant's
 * `appExperience.navigation.hamburger`, module-gated.
 */
interface TenantDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TenantDrawer({ open, onOpenChange }: TenantDrawerProps): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const linkedTenants = useAuthStore((s) => s.linkedTenants);
  const activeTenantId = useAuthStore((s) => s.tenantId);
  const branding = useBrandingStore((s) => s.branding);
  const hamburger = useExperienceStore((s) => s.getHamburger());
  const licensedModules = useModuleStore((s) => s.licensedModules);
  const isModuleEnabled = useModuleStore((s) => s.isModuleEnabled);

  // "Join another community" — build-config enabled and an SSO root scope
  // resolved (so there are sibling communities to search).
  const joinCfg = joinConfig();
  const canJoin = joinCfg.enabled && !!getEffectiveRootTenantId(env.rootTenantId);

  // Hamburger items the tenant's licence actually enables, sorted by order.
  const exploreItems = useMemo(
    () =>
      [...hamburger]
        .filter((item) => !item.module_required || isModuleEnabled(item.module_required))
        .sort((a, b) => a.order - b.order),
    [hamburger, licensedModules, isModuleEnabled],
  );

  const go = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === activeTenantId) {
      onOpenChange(false);
      return;
    }
    try {
      await switchTenant(tenantId);
      onOpenChange(false);
    } catch (error) {
      console.error('[drawer] tenant switch failed:', error);
    }
  };

  const handleSignOut = async () => {
    await useAuthStore.getState().signOut();
    onOpenChange(false);
    navigate('/login', { replace: true });
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName || humanizeEmail(user?.email) || 'Member';
  const initials = (fullName.charAt(0) || user?.email?.charAt(0) || '?').toUpperCase();
  // Only show the email line when it adds information beyond the name.
  const emailLine = user?.email && user.email !== displayName ? user.email : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      {/* ── Brand + profile hero ──────────────────────────────────── */}
      <div
        className="px-4 pb-5 pt-safe-or-6 text-white"
        style={{
          backgroundImage:
            'linear-gradient(150deg, var(--primary-700) 0%, var(--primary-900) 100%)',
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {branding?.logoSmallUrl ? (
              <img
                src={branding.logoSmallUrl}
                alt=""
                className="h-7 w-auto max-w-[150px] object-contain object-left"
              />
            ) : (
              <span className="truncate font-display text-headline">{branding?.name ?? 'Member'}</span>
            )}
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => onOpenChange(false)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => go('/app/profile')}
          className="mt-5 flex w-full items-center gap-3 text-left"
        >
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white font-display text-headline text-primary-700 shadow-sm"
            aria-hidden
          >
            {user?.profile_image_url && !avatarFailed ? (
              <img
                src={user.profile_image_url}
                alt=""
                className="h-full w-full object-cover"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              initials
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-body font-semibold">{displayName}</span>
            {emailLine ? (
              <span className="block truncate text-body-sm text-white/70">{emailLine}</span>
            ) : null}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-white/70" strokeWidth={2} />
        </button>
      </div>

      {/* ── Scrollable nav ────────────────────────────────────────── */}
      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {linkedTenants.length > 0 || canJoin ? (
          <DrawerSection title="Communities">
            {linkedTenants.map((tenant) => {
              const active = tenant.tenant_id === activeTenantId;
              return (
                <DrawerRow
                  key={tenant.tenant_id}
                  active={active}
                  label={tenant.name}
                  sublabel={tenant.region ?? tenant.domain}
                  onClick={() => void handleSwitch(tenant.tenant_id)}
                  leading={
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-sunken">
                      {tenant.branding?.logo_small_url ? (
                        <img
                          src={tenant.branding.logo_small_url}
                          alt=""
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-body-sm font-semibold text-ink-secondary">
                          {tenant.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                  }
                  trailing={
                    active ? (
                      <Check className="h-4 w-4 shrink-0 text-primary-700" strokeWidth={2.25} />
                    ) : undefined
                  }
                />
              );
            })}
            {canJoin ? (
              <DrawerRow
                dashed
                icon={<PlusCircle className="h-5 w-5" strokeWidth={1.75} />}
                label={joinCfg.entryLabel}
                onClick={() => go('/join')}
              />
            ) : null}
          </DrawerSection>
        ) : null}

        {exploreItems.length > 0 ? (
          <DrawerSection title="Explore">
            {exploreItems.map((item) => (
              <DrawerRow
                key={item.id}
                icon={<NavIcon item={item} />}
                label={item.label}
                onClick={() => go(routeForNavItem(item))}
              />
            ))}
          </DrawerSection>
        ) : null}

        <DrawerSection title="Account">
          <DrawerRow
            icon={<UserIcon className="h-5 w-5" strokeWidth={1.75} />}
            label="Profile"
            onClick={() => go('/app/profile')}
          />
          <DrawerRow
            icon={<Settings className="h-5 w-5" strokeWidth={1.75} />}
            label="Settings"
            onClick={() => go('/app/settings')}
          />
          <DrawerRow
            icon={<HelpCircle className="h-5 w-5" strokeWidth={1.75} />}
            label="Support"
            onClick={() => go('/app/support/requests')}
          />
        </DrawerSection>
      </nav>

      {/* ── Pinned sign-out footer ────────────────────────────────── */}
      <div className="border-t border-[color:var(--hairline)] px-3 pb-safe-or-6 pt-2">
        <DrawerRow
          danger
          noChevron
          icon={<LogOut className="h-5 w-5" strokeWidth={1.75} />}
          label="Sign out"
          onClick={() => void handleSignOut()}
        />
      </div>
    </Drawer>
  );
}

/* ── Building blocks ─────────────────────────────────────────────────── */

function DrawerSection({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="mb-5 last:mb-0">
      <p className="px-2.5 pb-1 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
        {title}
      </p>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function DrawerRow({
  icon,
  leading,
  label,
  sublabel,
  onClick,
  active,
  danger,
  dashed,
  trailing,
  noChevron,
}: {
  icon?: ReactNode;
  leading?: ReactNode;
  label: string;
  sublabel?: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  dashed?: boolean;
  trailing?: ReactNode;
  noChevron?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors',
        active ? 'bg-primary-50' : 'hover:bg-sunken active:bg-sunken',
        dashed && 'border border-dashed border-hairline-strong',
      )}
    >
      {leading ?? (
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            danger
              ? 'bg-danger/10 text-danger'
              : active
                ? 'bg-primary-600 text-white'
                : 'bg-sunken text-ink-secondary',
          )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-body',
            danger
              ? 'font-medium text-danger'
              : active
                ? 'font-semibold text-primary-700'
                : 'font-medium text-ink',
          )}
        >
          {label}
        </span>
        {sublabel ? (
          <span className="block truncate text-caption text-ink-muted">{sublabel}</span>
        ) : null}
      </span>
      {trailing ??
        (!noChevron && !danger ? (
          <ChevronRight
            className="h-4 w-4 shrink-0 text-ink-muted opacity-0 transition-opacity group-hover:opacity-100"
            strokeWidth={1.75}
          />
        ) : null)}
    </button>
  );
}

/** Leading glyph for a hamburger nav item — server-supplied art, else a glyph. */
function NavIcon({ item }: { item: CompiledNavigationItem }): JSX.Element {
  if (item.icon_url) {
    return <img src={item.icon_url} alt="" className="h-5 w-5" aria-hidden />;
  }
  return <LayoutGrid className="h-5 w-5" strokeWidth={1.75} />;
}

/** Resolve a nav item to an in-app route — explicit `route`, else module key. */
function routeForNavItem(item: CompiledNavigationItem): string {
  if (item.route) return item.route.startsWith('/') ? item.route : `/app/${item.route}`;
  return routeForModule(item.module_key) ?? routeForModule(item.module_required) ?? '/app/home';
}

/** Derive a friendly name from an email local-part: `jane.doe+x@…` → "Jane Doe". */
function humanizeEmail(email?: string): string {
  if (!email) return '';
  const local = email.split('@')[0]?.split('+')[0] ?? '';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
